import React, { useCallback } from 'react'
import type { ShapeSelectionChangeMeta } from '@/components/app/shapes/shape-manager'
import { clearCanvasPhotoSelectionIfAny } from '@/lib/photos/photos-panel-escape'
import { isShapeTextSupported } from '@/lib/shapes/shape-text-frame'
import { toast } from 'sonner'
import { checkpointsService } from '@/api/services/checkpoints.service'
import { logger } from '@/utils/logger'
import { clearPhotoPlacementOverrides } from '@/lib/photos/photo-placement-session'
import { clearBackgroundPlacementOverrides } from '@/lib/backgrounds/background-placement-session'
import { clearClikArtPlacementOverrides } from '@/lib/clikart/clikart-placement-session'
import { isSingleClikArtSelection } from '@/lib/clikart/is-single-clikart-selection'
import { isPlacementDragActive } from '@/lib/placement/placement-drag-activity'
import { resetClikArtPlacedIdsScan } from '@/api/hooks/use-clikart-placed-ids-scan'
import { clearTemplatePlacements } from '@/lib/templates/template-placement-session'
import { editorGuideSettingsService } from '@/api/services/editor-guide-settings.service'
import { lockedObjectBlocksCopy } from '@/lib/editor/clipboard-paste'
import { partitionSelectionForCut } from '@/lib/editor/cut-selection-policy'
import type { SpreadGuideSettingsLoaded } from '@/lib/guides/guide-settings-precedence'
import { tryUngroupSelection } from '@/lib/groups/ungroup-selection'
import { finalizeActiveShapeTextEdit } from '@/lib/shapes/shape-text-edit-session'
import { larryCloseMessage } from '@/lib/embed/host-messages'
import type {
  HomeDocumentLayout,
  HomeFoundation,
  HomeHistory,
  HomePersistence,
  HomeShapeEditing,
  HomeToolsAndSelection,
  HomeZoomAndSpread,
} from '../../types/home-groups'

/**
 * Commands the shell and panels invoke: logout, close and spread navigation, Select All / None,
 * group / ungroup, lock / freeze, clipboard with its menu enablement, Set Columns & Rows, and the
 * canvas / right-panel callbacks (tool activation, node edit, selection and click sync). The
 * callbacks that used to be inline JSX stay plain functions, re-created every render as before.
 */
export function useHomeMenuCommands(
  deps: Pick<
    HomeFoundation,
    | 'logout'
    | 'queryClient'
    | 'setMenuOpen'
    | 'setCurrentSpreadIndex'
    | 'textChains'
    | 'selectedTextBoxPanelIds'
    | 'navigate'
    | 'shapeManagerRef'
    | 'centerCanvasRef'
    | 'leftPanelRef'
  > &
    Pick<HomeZoomAndSpread, 'spreadId' | 'embedSession'> &
    Pick<
      HomeDocumentLayout,
      | 'setColumns'
      | 'setRows'
      | 'setColGutter'
      | 'setRowGutter'
      | 'setSpreadGuideSettings'
      | 'setSpreadGuideUpdatedAt'
    > &
    Pick<
      HomeToolsAndSelection,
      | 'setActiveObject'
      | 'setActiveObjects'
      | 'setActiveTextBoxes'
      | 'activeObjectsForMenus'
      | 'selectedTextBoxesForMenus'
      | 'setSelectedShape'
      | 'setSelectedPenTool'
      | 'selectedPointType'
      | 'setSelectedPointType'
      | 'isNodeEditMode'
      | 'setIsNodeEditMode'
      | 'setActiveObjectId'
      | 'activeItem'
      | 'setActiveItem'
      | 'setHasSelection'
      | 'hasObjectSelectionForMenus'
      | 'deactivatePanMode'
    > &
    Pick<
      HomePersistence,
      | 'flushQueue'
      | 'flushGroupActionQueue'
      | 'flushSpreadBackgroundWritesRef'
      | 'release'
      | 'applyActiveShapeSelection'
      | 'syncActiveFromShapeSelection'
    > &
    Pick<HomeHistory, 'flushPendingShapeDeleteHistory'> &
    Pick<HomeShapeEditing, 'revertPathConversionIfUnedited'>
) {
  const {
    logout,
    queryClient,
    setMenuOpen,
    setCurrentSpreadIndex,
    textChains,
    selectedTextBoxPanelIds,
    navigate,
    shapeManagerRef,
    centerCanvasRef,
    leftPanelRef,
    spreadId,
    embedSession,
    setColumns,
    setRows,
    setColGutter,
    setRowGutter,
    setSpreadGuideSettings,
    setSpreadGuideUpdatedAt,
    setActiveObject,
    setActiveObjects,
    setActiveTextBoxes,
    activeObjectsForMenus,
    selectedTextBoxesForMenus,
    setSelectedShape,
    setSelectedPenTool,
    selectedPointType,
    setSelectedPointType,
    isNodeEditMode,
    setIsNodeEditMode,
    setActiveObjectId,
    activeItem,
    setActiveItem,
    setHasSelection,
    hasObjectSelectionForMenus,
    deactivatePanMode,
    flushQueue,
    flushGroupActionQueue,
    flushSpreadBackgroundWritesRef,
    release,
    applyActiveShapeSelection,
    syncActiveFromShapeSelection,
    flushPendingShapeDeleteHistory,
    revertPathConversionIfUnedited,
  } = deps

  /**
   * Leaves the open spread with everything saved: finishes inline text, flushes both queues,
   * releases locks, lets background writes land, then exits the checkpoint. Shared by Logout and
   * the embedded Close.
   */
  const exitActiveSpread = useCallback(async () => {
    finalizeActiveShapeTextEdit(shapeManagerRef.current)
    flushPendingShapeDeleteHistory()
    await flushQueue()
    await flushGroupActionQueue()
    await release()
    if (spreadId) {
      try {
        // Background saves don't go through ChangeQueue — let any in flight land before exiting.
        await flushSpreadBackgroundWritesRef.current?.(spreadId)
        await checkpointsService.exitCheckpoint(spreadId)
      } catch (err) {
        logger.warn('exit-checkpoint failed:', err)
      }
    }
  }, [
    flushPendingShapeDeleteHistory,
    flushQueue,
    flushGroupActionQueue,
    flushSpreadBackgroundWritesRef,
    release,
    spreadId,
    shapeManagerRef,
  ])

  const handleLogout = useCallback(async () => {
    await exitActiveSpread()

    // Session-scoped photo-, ClikArt-, background- and template-placement records are only valid for THIS
    // login — a fresh login gets fresh server-computed `placedFlag` data from the backend's
    // book-wide scan (see `photo-placement-session.ts`, `clikart-placement-session.ts`,
    // `background-placement-session.ts`).
    clearPhotoPlacementOverrides()
    clearClikArtPlacementOverrides()
    resetClikArtPlacedIdsScan(queryClient)
    clearBackgroundPlacementOverrides()
    clearTemplatePlacements()

    logout()
    navigate({ to: '/login' })
  }, [exitActiveSpread, logout, navigate, queryClient])

  const handleMenuToggle = (isOpen: boolean) => {
    setMenuOpen(isOpen)
  }

  const isClosingRef = React.useRef(false)
  // The ClikArt the last selection change left as the only selection, or null. The Left Panel opens
  // when this changes to a ClikArt and hides when the selection empties after one (WMPCM-13378).
  const selectedClikArtIdRef = React.useRef<string | null>(null)

  // Close only means something inside the host's iframe (Mary's spread ladder): save, then ask the
  // host to remove the editor. The standalone editor has nowhere to close to, so Close does nothing.
  const handleClose = () => {
    if (!embedSession || isClosingRef.current) return
    isClosingRef.current = true

    void (async () => {
      try {
        await exitActiveSpread()
      } catch (err) {
        // Keep the editor open rather than drop unsaved edits; Close can be tried again.
        isClosingRef.current = false
        logger.error('Saving before closing the embedded editor failed:', err)
        toast.error("Your changes couldn't be saved. Try closing again.")
        return
      }

      embedSession.postToHost(larryCloseMessage(spreadId || embedSession.spreadId))
    })()
  }

  const handleNavigate = (direction: 'prev' | 'next') => {
    setCurrentSpreadIndex((p) => (direction === 'prev' ? Math.max(0, p - 1) : p + 1))
  }

  const syncActiveToAllShapes = React.useCallback(() => {
    const shapes = shapeManagerRef.current?.getShapes?.() ?? []

    setActiveObjects(shapes)
    setActiveObjectId(shapes.length === 1 ? (shapes[0]?.id ?? null) : null)
  }, [setActiveObjects, setActiveObjectId, shapeManagerRef])

  const clearActiveObject = React.useCallback(() => {
    setActiveObject(null)
    setActiveObjectId(null)
    setActiveTextBoxes([])
  }, [setActiveObject, setActiveObjectId, setActiveTextBoxes])

  const handleSelectAll = React.useCallback(() => {
    // Menu → Edit → Select All is reachable MID-EDIT: the whole menubar dropdown carries
    // `data-text-edit-keepalive`, so opening it does not end the text session (WMPCM-6867). Without
    // this the row select-alled every OBJECT on the spread while the user was mid-sentence, and the
    // Ctrl+X that usually follows then cut the entire spread — the same failure WMPCM-9396 fixed on
    // the Ctrl+A keyboard path and only there. Dispatched through the canvas handle so the menu and
    // the shortcut cannot disagree, exactly as `Delete` and `Cut` already do; the row is
    // deliberately NOT disabled, because Select All is always meaningful — it just has to mean the
    // right thing.
    if (centerCanvasRef.current?.selectAllForActiveMode()) return

    shapeManagerRef.current?.selectAll()
    syncActiveToAllShapes()
    if (activeItem === 'shape') {
      setActiveItem('select')
      setSelectedShape(null)
    }
  }, [
    syncActiveToAllShapes,
    activeItem,
    centerCanvasRef,
    shapeManagerRef,
    setActiveItem,
    setSelectedShape,
  ])

  const handleSelectNone = React.useCallback(() => {
    centerCanvasRef.current?.clearSelection()
    clearActiveObject()
  }, [clearActiveObject, centerCanvasRef])

  const handleGroupObjects = React.useCallback(() => {
    shapeManagerRef.current?.groupSelectedObjects()
  }, [shapeManagerRef])

  const handleUngroupObjects = React.useCallback(() => {
    const sm = shapeManagerRef.current

    if (!sm) return

    tryUngroupSelection(sm, (message) => toast.error(message))
  }, [shapeManagerRef])

  const handleLockSelectedObjects = React.useCallback(() => {
    centerCanvasRef.current?.lockSelected()
  }, [centerCanvasRef])

  const handleUnlockSelectedObjects = React.useCallback(() => {
    centerCanvasRef.current?.unlockSelected()
  }, [centerCanvasRef])

  const handleUnlockAllObjects = React.useCallback(() => {
    centerCanvasRef.current?.unlockAll()
  }, [centerCanvasRef])

  const handleFreezeSelectedObjects = React.useCallback(() => {
    centerCanvasRef.current?.freezeSelected()
  }, [centerCanvasRef])

  const handleUnfreezeSelectedObjects = React.useCallback(() => {
    centerCanvasRef.current?.unfreezeSelected()
  }, [centerCanvasRef])

  const handleUnfreezeAllObjects = React.useCallback(() => {
    centerCanvasRef.current?.unfreezeAll()
  }, [centerCanvasRef])

  const handleCutSelection = React.useCallback(() => {
    centerCanvasRef.current?.cutSelection()
  }, [centerCanvasRef])

  const handleCopySelection = React.useCallback(() => {
    centerCanvasRef.current?.copySelection()
  }, [centerCanvasRef])

  const handleDuplicateSelection = React.useCallback(() => {
    centerCanvasRef.current?.duplicateSelection()
  }, [centerCanvasRef])

  /**
   * Copy-only carve-out: a locked *linked* frame stays copyable (WMPCM-7012 AC3), so Copy can't share
   * `hasLockedSelectionForMenus` with Cut/Delete.
   */
  const copyDisabledForMenus =
    !hasObjectSelectionForMenus ||
    activeObjectsForMenus.some((shape) => lockedObjectBlocksCopy(shape, textChains)) ||
    selectedTextBoxesForMenus.some((box) => lockedObjectBlocksCopy(box, textChains))

  // Cut skips protected *linked* frames instead of aborting the gesture, so the row must stay enabled
  // while any member is still cuttable — `hasLockedSelectionForMenus` would hide the partial cut
  // (WMPCM-8663). The partition also reports `empty`, which covers the no-selection case, and gates on
  // frozen — something Cut never did before, leaving an enabled row that only failed at runtime.
  const cutDisabledForMenus =
    partitionSelectionForCut({
      shapes: activeObjectsForMenus,
      textBoxes: selectedTextBoxesForMenus,
      chains: textChains,
    }).kind !== 'proceed'

  const handlePasteSelection = React.useCallback(() => {
    centerCanvasRef.current?.pasteSelection()
  }, [centerCanvasRef])

  const handlePasteSelectionInPlace = React.useCallback(() => {
    centerCanvasRef.current?.pasteSelectionInPlace()
  }, [centerCanvasRef])

  const handleDeleteSelection = React.useCallback(() => {
    centerCanvasRef.current?.deleteSelection()
  }, [centerCanvasRef])

  // Canvas and panel callbacks that used to be written inline in the JSX. Deliberately plain
  // functions, re-created every render exactly as the inline arrows were — not `useCallback` —
  // so the identity the children see does not change.
  const handleSetColumnsRows = async ({
    columns: c,
    rows: r,
    colGutter: cg,
    rowGutter: rg,
  }: {
    columns: number
    rows: number
    colGutter: number
    rowGutter: number
  }) => {
    await Promise.all([
      editorGuideSettingsService.updateColumnGuides(spreadId, {
        count: c,
        gutterWidth: cg,
      }),
      editorGuideSettingsService.updateRowGuides(spreadId, {
        count: r,
        gutterWidth: rg,
      }),
    ])

    const nextGuideSettings: SpreadGuideSettingsLoaded = {
      columns: c,
      rows: r,
      colGutter: cg,
      rowGutter: rg,
      hasColumns: true,
      hasRows: true,
    }
    const nowIso = new Date().toISOString()

    setSpreadGuideSettings(nextGuideSettings)
    setSpreadGuideUpdatedAt({ column: nowIso, row: nowIso })
    setColumns(c)
    setRows(r)
    setColGutter(cg)
    setRowGutter(rg)
  }

  const handleNodeEditPointTypeChange = (type: 'smooth' | 'corner') => {
    setSelectedPointType(type === 'smooth' ? 'curve' : 'straight')
  }

  const handleExitPenTool = () => {
    setSelectedPenTool(null)
    // Reset node-edit mode in case we were in a continue-path flow
    // (handleContinuePath does not call onNodeEditModeChange to preserve the
    // pen tool, so we clean up here if the user exits mid-continuation).
    setIsNodeEditMode(false)
  }

  const handleToggleNodeEditPenMode = (active: boolean) => {
    if (active) {
      setSelectedPenTool('pen')
    } else {
      setSelectedPenTool(null)
    }
  }

  const handleNodeEditModeChange = (editing: boolean, shapeId?: string) => {
    setIsNodeEditMode(editing)
    if (editing && !selectedPointType) {
      setSelectedPointType('curve')
    }

    if (!editing) {
      if (shapeId) revertPathConversionIfUnedited(shapeId)
      setSelectedPenTool(null)
      // Don't reset the tool or clear selectedShape if the user has already
      // switched to a different tool (e.g. selected a shape to draw while in
      // node-edit mode — that tool switch is what triggered this callback, so
      // it should take precedence over the node-edit exit cleanup).
      if (activeItem !== 'shape' && activeItem !== 'type') {
        setActiveItem('select')
        setSelectedShape(null)
      }
    }
  }

  const handleCanvasBackgroundClick = () => {
    // Blur any focused input so Object Panel commit (unit conversion) runs before selection clears
    const el = document.activeElement as HTMLElement | null

    if (el?.blur && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) {
      el.blur()
    }

    setActiveObject(null)
    setActiveObjects([])
    setActiveObjectId(null)
    setActiveTextBoxes([])
    // Deliberately doesn't touch the left panel: with nothing selected there is nothing for it to
    // show, and revealing it here reopened a panel the user had collapsed on every empty-canvas
    // click (and Escape, which lands here too). It opens again when an object is selected
    // (`revealSelectTool`) or a tool is chosen.
  }

  const handleCanvasSelectionChange = (shapeIds: string[], meta?: ShapeSelectionChangeMeta) => {
    const tIds = selectedTextBoxPanelIds
    const totalSelectedCount = shapeIds.length + tIds.length

    setHasSelection(totalSelectedCount > 0)

    // A ClikArt shows its info in the Left Panel (WMPCM-11032), so the panel follows the selection:
    // it opens when a ClikArt becomes the selection however that happened (a click already reveals
    // it, a drop selects the new ClikArt without one), and goes away when the selection empties.
    //
    // Opens on a change of ClikArt, not on every callback. ShapeManager notifies whenever its
    // selection array is replaced, even with the same ids, so the same ClikArt can be reported
    // again; reopening then would undo a panel the user collapsed with that ClikArt selected.
    const selectedClikArtId =
      tIds.length === 0 && meta?.shapes != null && isSingleClikArtSelection(meta.shapes)
        ? meta.shapes[0].id
        : null

    if (selectedClikArtId !== null) {
      if (selectedClikArtId !== selectedClikArtIdRef.current) {
        leftPanelRef.current?.revealSelectTool?.()
      }
    } else if (
      totalSelectedCount === 0 &&
      selectedClikArtIdRef.current !== null &&
      // Starting a placement drag clears the selection too, and that is not the user deselecting:
      // hiding then would close the panel (and resize the canvas) mid-drag, only to reopen on drop.
      !isPlacementDragActive()
    ) {
      leftPanelRef.current?.hideSelectToolPanel?.()
    }

    // Only a change that reports its shapes says what is selected now; meta is optional, and an
    // event without it must not make the next empty selection look like it follows nothing.
    if (meta?.shapes != null) {
      selectedClikArtIdRef.current = selectedClikArtId
    }

    if (totalSelectedCount > 1 && shapeIds.length > 0) {
      // Which section opens is the auto-open hook's call (Object for a mixed/multi-shape
      // selection), so it can respect a section the user closed for this selection.
      leftPanelRef.current?.revealSelectTool?.()
    }

    // Prefer the shapes resolved inside ShapeManager. A live `getShapes()` read is
    // impossible here: this fires from ShapeManager's useLayoutEffect, during which
    // its imperative handle is detached, so `syncActiveFromShapeSelection` bails at
    // its `if (!mgr) return` and leaves `activeObjects` holding an already
    // deselected shape. That stale entry inflates the Object panel's selection count
    // and makes a single text frame look like a mixed multi-selection — which is why
    // panel Rotate was rotating two elements at once.
    if (meta?.shapes) {
      applyActiveShapeSelection(meta.shapes)
    } else {
      syncActiveFromShapeSelection()
    }
  }

  const handleSelectAllApplied = () => {
    syncActiveToAllShapes()
    if (activeItem === 'shape') {
      setActiveItem('select')
      setSelectedShape(null)
    }
  }

  const handleCanvasShapeClick = (shapeId: string) => {
    const shapes = shapeManagerRef.current?.getShapes?.() ?? []
    const found = shapes.find((s) => s.id === shapeId) ?? null

    if (found) {
      if (found.locked !== true) {
        // Not `expandAndOpenObject`: the clicked object's own section opens via
        // `usePropertiesPanelAutoOpenSection` (Type for text, Photo for a photo, Object for a
        // shape), and forcing Object here reopened it on every click, even after the user closed it.
        leftPanelRef.current?.revealSelectTool?.()
      }

      // Derive from the live manager selection instead of calling
      // setActiveObject(found): that replaces the selection with this one shape,
      // so a shift/ctrl+click that grew the selection to N was collapsing the
      // panels' view of it back to the last-clicked shape — property panels then
      // applied edits to that shape alone.
      syncActiveFromShapeSelection()
    }
  }

  const handleActivateTextTool = () => {
    deactivatePanMode()
    setActiveItem('type')
    setSelectedShape(null)
    leftPanelRef.current?.expandAndOpenType?.({ focusToolButton: false })

    // See the matching comment in `handleActivateTool` — the T-key shortcut is a
    // separate entry point to the same "activate Type tool" behavior and needs the
    // same fix: leaving node-edit mode active (and its selection cleared) would
    // otherwise make the shortcut below find nothing selected.
    if (isNodeEditMode) {
      centerCanvasRef.current?.exitNodeEditMode()
    }

    const mgr = shapeManagerRef.current
    const selectedShapeIds = mgr?.getSelectedShapeIds() ?? []

    if (selectedShapeIds.length === 1) {
      const shapeId = selectedShapeIds[0]!
      const shape = mgr?.getShapes().find((s) => s.id === shapeId)

      if (shape && !shape.locked && isShapeTextSupported(shape)) {
        mgr?.enterShapeTextEdit(shapeId)
      }
    }
  }

  const handleExpandTypePanel = () => {
    setActiveItem('type')
    leftPanelRef.current?.expandAndOpenType?.({ focusToolButton: false })
  }

  const handleActivateSelectTool = () => {
    deactivatePanMode()
    setActiveItem('select')
    setSelectedShape(null)
  }

  const handleRightPanelOpened = () => {
    // WMPCM-11615 AC 3 (https://walsworth.atlassian.net/browse/WMPCM-11615): "opening a
    // right panel should retain the current object selection and leave its property
    // sections unchanged". So no `clearSelection()` here: only the left panel's tool and
    // no-selection display are reset. Switching to Select still ends an active text edit
    // (`handleActivateTool` finalizes it) while keeping the frame selected.
    leftPanelRef.current?.handleRightPanelOpened?.()
  }

  const handleRightPanelEscapeFallbackToSelectTool = () => {
    // WMPCM-11088 AC-5: second Escape on the Photos rail icon activates Select
    // with visible focus on the Select tool button.
    leftPanelRef.current?.focusSelectTool?.()
  }

  const handleRightPanelEscapeClearPhotoSelection = () => {
    if (!clearCanvasPhotoSelectionIfAny(shapeManagerRef.current)) return false

    leftPanelRef.current?.handleRightPanelOpened?.()
    return true
  }

  return {
    handleLogout,
    handleMenuToggle,
    handleClose,
    handleNavigate,
    syncActiveToAllShapes,
    clearActiveObject,
    handleSelectAll,
    handleSelectNone,
    handleGroupObjects,
    handleUngroupObjects,
    handleLockSelectedObjects,
    handleUnlockSelectedObjects,
    handleUnlockAllObjects,
    handleFreezeSelectedObjects,
    handleUnfreezeSelectedObjects,
    handleUnfreezeAllObjects,
    handleCutSelection,
    handleCopySelection,
    handleDuplicateSelection,
    copyDisabledForMenus,
    cutDisabledForMenus,
    handlePasteSelection,
    handlePasteSelectionInPlace,
    handleDeleteSelection,
    handleSetColumnsRows,
    handleNodeEditPointTypeChange,
    handleExitPenTool,
    handleToggleNodeEditPenMode,
    handleNodeEditModeChange,
    handleCanvasBackgroundClick,
    handleCanvasSelectionChange,
    handleSelectAllApplied,
    handleCanvasShapeClick,
    handleActivateTextTool,
    handleExpandTypePanel,
    handleActivateSelectTool,
    handleRightPanelOpened,
    handleRightPanelEscapeFallbackToSelectTool,
    handleRightPanelEscapeClearPhotoSelection,
  }
}
